import { api } from "@/lib/api";

export type IssueStatus =
  | "backlog"
  | "todo"
  | "in_progress"
  | "in_review"
  | "done"
  | "blocked"
  | "cancelled";

export type IssuePriority = "critical" | "high" | "medium" | "low";

export type Issue = {
  id: string;
  companyId: string;
  projectId: string | null;
  goalId: string | null;
  title: string;
  description: string | null;
  status: IssueStatus;
  priority: IssuePriority;
  assigneeAgentId: string | null;
  assigneeUserId: string | null;
  identifier: string | null;
  issueNumber: number | null;
  templateKey: string | null;
  productId: string | null;
  parentId: string | null;
  /** "manual" pra issues criadas pelo user/agente direto, "routine_execution"
   *  pra issues geradas automaticamente por routines. */
  originKind?: string;
  originId?: string | null;
  startedAt: string | null;
  completedAt: string | null;
  /** Data de publicação planejada (posts de calendário editorial). */
  scheduledFor: string | null;
  /** Arte marcada como "estilo da marca" — vira âncora de estilo das próximas
   *  gerações de arte daquela marca. */
  isBrandStyleAnchor?: boolean;
  createdAt: string;
  updatedAt: string;
  /** Estado de execução (jsonb). Guarda metadata efêmera da tarefa, incluindo
   *  o `artBriefing` quando o card gera arte — usado pra reabrir a tarefa com
   *  o briefing pré-preenchido. */
  executionState?: {
    artBriefing?: {
      destaque?: string;
      logo?: string;
      objetivo?: string;
      clima?: string;
      pessoa?: string;
      especifico?: string;
    };
    knowledgeBaseIds?: string[];
    /** Setado quando o usuário reprova E encerra a tarefa (vira done com badge
     *  "Reprovado"). Diferente do "Ajustar", que regenera. */
    rejectedAndClosed?: boolean;
    rejectedReason?: string;
    rejectedAt?: string;
    [key: string]: unknown;
  } | null;
  labelIds?: string[];
  labels?: IssueLabel[];
};

export type IssueLabel = {
  id: string;
  companyId: string;
  name: string;
  color: string;
  createdAt: string;
  updatedAt: string;
};

export type IssueListFilters = {
  status?: string;
  projectId?: string;
  labelId?: string;
  assigneeAgentId?: string;
  parentId?: string;
  q?: string;
  limit?: number;
  /** Por padrão a API esconde tasks geradas por routines. Setar true pra
   *  incluir (Kanban precisa pra mostrar coluna "Automação"). */
  includeRoutineExecutions?: boolean;
};

function buildQuery(filters?: IssueListFilters) {
  if (!filters) return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === "") continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

type IssueListResponse = { issues?: Issue[] } | Issue[];

export const issuesApi = {
  list: async (companyId: string, filters?: IssueListFilters): Promise<Issue[]> => {
    const result = await api.get<IssueListResponse>(
      `/companies/${companyId}/issues${buildQuery(filters)}`,
    );
    if (Array.isArray(result)) return result;
    return result.issues ?? [];
  },

  labels: (companyId: string) => api.get<IssueLabel[]>(`/companies/${companyId}/labels`),

  updateStatus: (issueId: string, status: IssueStatus) =>
    api.patch<Issue>(`/issues/${issueId}`, { status }),

  /** Move a tarefa pra outro espaço (projectId). null = Geral (sem espaço). */
  updateProject: (issueId: string, projectId: string | null) =>
    api.patch<Issue>(`/issues/${issueId}`, { projectId }),

  /** Inicia uma tarefa que está em backlog (ex: post do calendário). */
  start: (companyId: string, issueId: string) =>
    api.post<{ started: boolean; status?: string }>(
      `/companies/${companyId}/issues/${issueId}/start`,
      {},
    ),

  delete: (issueId: string) => api.delete<void>(`/issues/${issueId}`),

  detail: (issueId: string) => api.get<Issue>(`/issues/${issueId}`),

  documents: (issueId: string) =>
    api.get<IssueDocument[]>(`/issues/${issueId}/documents`),

  /** Aprova um entregável em revisão. Move pra "done".
   *  Quando o template tem nextTemplateKey, o backend cria automaticamente
   *  sub-task(s) com `parentId` apontando pra esta. Se selectedVariations
   *  é passada, cria UMA subtask por variação selecionada. Caso contrário,
   *  uma subtask genérica. */
  approve: (
    issueId: string,
    options: {
      selectedVariations?: Array<{ label: string; fullText: string }>;
      /**
       * Controla criação de sub-task pra próxima etapa do funil:
       *  - undefined ou true (default): cria sub-task se template tem
       *    nextTemplateKey configurado
       *  - false: aprova SEM criar próxima etapa (user escolheu finalizar
       *    aqui ou decidir depois). Evita gastar token gerando arte/etc
       *    que o cliente não vai usar.
       */
      createNextStep?: boolean;
      /**
       * Asset IDs selecionados pelo user no Picker UI antes de aprovar copy
       * que vira sub-task de arte. Esses assets viram reference images
       * pra Gemini gerar a arte com identidade preservada.
       */
      referenceAssetIds?: string[];
    } = {},
  ) =>
    api.patch<Issue & { chainedSubtaskId?: string; chainedSubtaskIds?: string[] }>(
      `/issues/${issueId}`,
      {
        status: "done",
        ...(options.selectedVariations && options.selectedVariations.length > 0
          ? { selectedVariations: options.selectedVariations }
          : {}),
        ...(options.createNextStep === false ? { createNextStep: false } : {}),
        ...(options.referenceAssetIds && options.referenceAssetIds.length > 0
          ? { referenceAssetIds: options.referenceAssetIds }
          : {}),
      },
    ),

  /** Lista variações detectadas no documento principal pra mostrar modal de
   *  seleção quando user vai aprovar uma copy com múltiplas opções. */
  variations: (issueId: string) =>
    api.get<{
      variations: Array<{ label: string; preview: string; fullText: string; startIndex: number }>;
      hasMultiple: boolean;
    }>(`/issues/${issueId}/variations`),

  /**
   * Rejeita um entregável em revisão. Posta o motivo como comentário pra
   * o agente reler e move a issue de volta pra "todo" pro heartbeat
   * pegar com o feedback novo.
   */
  reject: (issueId: string, reason: string) =>
    api.patch<Issue>(`/issues/${issueId}`, {
      status: "todo",
      comment: `[REPROVADO PELO USUÁRIO] ${reason.trim()}`,
    }),

  /**
   * Reprova E ENCERRA o entregável (diferente de `reject`, que volta pra fila e
   * regenera). Move pra "done" marcado como reprovado (badge na UI) e registra o
   * motivo pra automação noturna aprender. O usuário começa do zero numa tarefa nova.
   */
  rejectClose: (issueId: string, reason: string) =>
    api.post<Issue>(`/issues/${issueId}/reject-close`, { reason: reason.trim() }),

  /**
   * Desbloqueia uma issue em status=blocked. Posta a mensagem do user
   * (com URLs de assets, instruções, etc) e transiciona pra todo. O
   * agente acorda e retoma com o contexto novo.
   */
  unblock: (issueId: string, message: string) =>
    api.post<Issue>(`/issues/${issueId}/unblock`, { message }),

  /**
   * Reabre uma tarefa concluída (status=done) pra gerar uma NOVA versão do
   * entregável em cima do mesmo histórico (campanha nova, teste A/B de copy).
   * O `artBriefing` ajustado pelo usuário chega aqui e direciona a regeneração;
   * a nova arte entra como versão N+1 (as anteriores ficam preservadas no
   * histórico). Volta o card pra fila e re-roda o pipeline de geração.
   */
  /**
   * Edição por imagem (lápis): regenera SÓ as imagens marcadas com uma
   * instrução, preservando as outras. Cria uma versão N+1 do card e volta
   * pra revisão. Usado na galeria de qualquer card de arte.
   */
  editImages: (
    issueId: string,
    edits: Array<{ attachmentId: string; instruction: string }>,
  ) => api.post<Issue>(`/issues/${issueId}/edit-images`, { edits }),

  reopen: (
    issueId: string,
    artBriefing?: {
      destaque?: string;
      logo?: string;
      objetivo?: string;
      clima?: string;
      pessoa?: string;
      especifico?: string;
    } | null,
  ) =>
    api.post<Issue>(`/issues/${issueId}/reopen`, {
      artBriefing: artBriefing ?? null,
    }),

  /** Reinicia a geração de uma tarefa presa (carregando) ou bloqueada. */
  retry: (issueId: string) =>
    api.post<{ ok: boolean; status: string }>(`/issues/${issueId}/retry`, {}),

  /** Cancela uma tarefa presa/em criação/bloqueada → status "cancelado". */
  cancel: (issueId: string) => api.post<Issue>(`/issues/${issueId}/cancel`, {}),

  /** Marca/desmarca esta arte como "estilo da marca" (âncora de estilo das
   *  próximas gerações). */
  setBrandStyleAnchor: (issueId: string, value: boolean) =>
    api.post<Issue>(`/issues/${issueId}/brand-style-anchor`, { value }),

  /**
   * Promove o documento aprovado dessa issue em base de conhecimento
   * reusável. Backend reformata o texto via Haiku pro formato KB factual
   * (denso, estruturado), evitando que cada task precise rerodar a mesma
   * pesquisa. Idempotente — se já promovida, atualiza a KB existente.
   */
  promoteToKb: (
    issueId: string,
    options: { kbType?: string; nameOverride?: string } = {},
  ) =>
    api.post<{
      kbId: string;
      kbName: string;
      isNew: boolean;
      costCents: number;
      inputTokens: number;
      outputTokens: number;
    }>(`/issues/${issueId}/promote-to-kb`, options),

  /** Lista comentários da issue (todos os atores). */
  comments: (issueId: string) =>
    api.get<IssueComment[]>(`/issues/${issueId}/comments`),

  /** Lista revisões do documento principal (key="main"). */
  documentRevisions: (issueId: string) =>
    api.get<
      Array<{
        id: string;
        revisionNumber: number;
        title: string | null;
        body: string;
        changeSummary: string | null;
        createdAt: string;
      }>
    >(`/issues/${issueId}/documents/main/revisions`),

  /** Votos de feedback (👍/👎 + comentário) do usuário atual nesta issue. */
  feedbackVotes: (issueId: string) =>
    api.get<
      Array<{
        id: string;
        targetType: "issue_comment" | "issue_document_revision";
        targetId: string;
        vote: "up" | "down";
        reason: string | null;
        createdAt: string;
        updatedAt: string;
      }>
    >(`/issues/${issueId}/feedback-votes`),

  /** Salva (upsert) um voto de feedback num alvo (revisão de documento ou comentário). */
  saveFeedbackVote: (
    issueId: string,
    body: {
      targetType: "issue_comment" | "issue_document_revision";
      targetId: string;
      vote: "up" | "down";
      reason?: string;
    },
  ) => api.post<unknown>(`/issues/${issueId}/feedback-votes`, body),

  /** Lista attachments da issue. Usado pra renderizar imagens geradas. */
  attachments: (issueId: string) =>
    api.get<IssueAttachment[]>(`/issues/${issueId}/attachments`),

  /**
   * Sobe arquivo como attachment de uma issue. Retorna o attachment
   * incluindo `contentPath` que aponta pra `/api/attachments/<id>/content`.
   */
  uploadAttachment: async (
    companyId: string,
    issueId: string,
    file: File,
  ): Promise<IssueAttachment> => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(
      `/api/companies/${companyId}/issues/${issueId}/attachments`,
      { method: "POST", credentials: "include", body: formData },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(
        (err as { error?: string } | null)?.error ?? `Upload failed: ${res.status}`,
      );
    }
    return res.json();
  },
};

export type IssueAttachment = {
  id: string;
  issueId: string;
  contentType: string;
  byteSize: number;
  originalFilename: string | null;
  contentPath: string;
  /** Versão do entregável. Artes versionam a cada re-entrega; refs ficam em 1. */
  version: number;
  createdAt: string;
};

export type IssueComment = {
  id: string;
  issueId: string;
  body: string;
  authorAgentId: string | null;
  authorUserId: string | null;
  createdAt: string;
};

export type IssueDocumentFormat = "document" | "gallery" | "markdown" | string;

export type IssueDocument = {
  id: string;
  issueId: string;
  documentId: string;
  key: string;
  title: string | null;
  format: IssueDocumentFormat;
  body?: string;
  latestBody?: string;
  latestRevisionNumber: number;
  createdAt: string;
  updatedAt: string;
};
